import { memo, useState, useCallback } from 'react';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Separator,
  useForm,
  zodResolver,
} from '@flote/ui';
import { useAuthContext, useAnalytics, AnalyticsEvents } from '@flote/app-core';
import type { CreateB2BWaveCustomerDto } from '@flote/types/customer';
import { toStateAbbreviation } from '@flote/utils';
import { toast } from 'sonner';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useModal, ModalType } from '../../lib/modal';

const formSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  companyNumber: z.string().min(1, 'Company number is required'),
  activity: z.string().min(1, 'Please select an activity'),
  fullName: z.string().min(1, 'Full name is required'),
  phone: z.string().min(1, 'Phone number is required'),
  email: z.string().email('Please enter a valid email'),
  address: z.string().min(1, 'Address is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  country: z.string().min(1, 'Country is required'),
  postalCode: z.string().min(1, 'Postal code is required'),
});

type FormData = z.infer<typeof formSchema>;

const activities = ['Brand', 'Buyer', 'Brand/Buyer', 'Other'];

const states = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
];

const countries = ['United States'];

interface RegistrationModalProps {
  userEmail?: string;
}

export const RegistrationModal = memo(({ userEmail }: RegistrationModalProps) => {
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { closeModal, openModal } = useModal();
  const { createCustomer } = useAuthContext();
  const { track } = useAnalytics();

  const handleClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleBackToAuth = useCallback(() => {
    closeModal();
    openModal(ModalType.AUTH_MODAL, {});
  }, [closeModal, openModal]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: '',
      companyNumber: '',
      activity: '',
      fullName: '',
      phone: '',
      email: userEmail || '',
      address: '',
      addressLine2: '',
      city: '',
      state: '',
      country: 'United States',
      postalCode: '',
    },
  });

  const onSubmit = useCallback(
    async (data: FormData) => {
      setIsLoading(true);
      if (!recaptchaVerified) {
        toast.error('Please verify that you are not a robot');
        setIsLoading(false);
        return;
      }

      const createCustomerInput: CreateB2BWaveCustomerDto = {
        email: data.email,
        companyName: data.companyName,
        companyNumber: data.companyNumber,
        companyActivities: data.activity,
        name: data.fullName,
        vatGroupId: 1,
        pricelistId: 1,
        phone: data.phone,
        address: data.address,
        city: data.city,
        province: toStateAbbreviation(data.state),
        country: data.country === 'United States' ? 'US' : data.country,
        postalCode: data.postalCode,
        extraFields: {},
        paymentWays: [],
        address2: data.addressLine2 ?? '',
        taxId: '',
        website: '',
        approved: false,
        disableOrdering: true,
        canConfirmOrder: false,
      };

      try {
        const result = await createCustomer(createCustomerInput);
        if (result.body) {
          track(AnalyticsEvents.ACCOUNT_CREATED, {
            email: data.email,
            companyName: data.companyName,
          });
          toast.success('Account created successfully');
          setSubmitted(true);
        } else {
          toast.error('Account creation failed');
        }
      } catch (error) {
        console.error('Create customer error:', error);
        toast.error('Account creation failed');
      } finally {
        setIsLoading(false);
      }
    },
    [recaptchaVerified, createCustomer, track]
  );

  if (submitted) {
    return (
      <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-lg">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Registration Successful!</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto text-center">
              Thank you for submitting your registration. You can close this dialog and use the email
              address you provided to login.
            </p>
            <Button onClick={handleBackToAuth} className="w-full max-w-xs">
              Sign In
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Register your account</DialogTitle>
          <DialogDescription>
            Fill out the form below to create your wholesale account.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Company Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Company Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="companyNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Montana CCD License Number *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="activity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Activity *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your business activity..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {activities.map((activity) => (
                          <SelectItem key={activity} value={activity}>
                            {activity}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Personal Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number *</FormLabel>
                      <FormControl>
                        <Input {...field} type="tel" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Address Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Address Information</h3>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street Address *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="addressLine2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address Line 2 (Optional)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {states.map((state) => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postal Code *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Verification and Submit */}
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 flex items-center space-x-3 hover:border-gray-300 transition-colors">
                  <input
                    type="checkbox"
                    id="recaptcha"
                    checked={recaptchaVerified}
                    onChange={(e) => setRecaptchaVerified(e.target.checked)}
                    className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="recaptcha" className="text-sm font-medium text-gray-700 cursor-pointer">
                    I'm not a robot
                  </label>
                  <div className="ml-auto">
                    <div className="text-xs text-gray-500 font-medium">reCAPTCHA</div>
                    <div className="text-xs text-gray-400">Privacy - Terms</div>
                  </div>
                </div>
              </div>

              <div className="flex w-full justify-between pt-4 gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBackToAuth}
                  className="w-full"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});

RegistrationModal.displayName = 'RegistrationModal';
